import { useState, memo, useMemo, useEffect } from "react"
import ErrorLogsTable from "./components/ErrorLogsTable"
import usePagination from "../../hooks/usePagination"
import ApiError from "../../components/ApiError"
import Dropdown from "../../components/Dropdown"
import { useErrorLogsData } from "../../hooks/useErrorLogs"
import { generateFilter } from "../../utils"
import { getDateRange } from "../../utils/datetime"
import { bodyParts } from "../../constants"

const ErrorLogs = () => {
  const { limit, onPaginationChange, page, pagination } = usePagination()
  const [filters, setFilters] = useState([])

  const getInitialValue = (key, defaultValue) => {
    const stored = sessionStorage.getItem("queryObject")
    if (!stored) return defaultValue

    try {
      const parsed = JSON.parse(stored)
      return parsed[key] ?? defaultValue
    } catch (err) {
      return defaultValue
    }
  }

  const [source, setSource] = useState(() =>
    getInitialValue("source", "Ramsoft")
  )
  const [bodyPart, setBodyPart] = useState(() =>
    getInitialValue("bodyPart", "Brain")
  )

  const handleUpdateBodyPart = (value) => {
    setBodyPart(value)
    if (value === bodyParts.SHOULDER) {
      setView("Lateral")
    }
    if ([bodyParts.SPINE, bodyParts.KNEE].includes(value)) {
      setView("Sagittal")
    }
  }
  const [date, setDate] = useState(() => getInitialValue("date", "Today"))
  const [type, setType] = useState(() => getInitialValue("type", "All"))
  const [view, setView] = useState(() => getInitialValue("view", "Sagittal"))
  const [region, setRegion] = useState(() => getInitialValue("region", "All"))
  const dateValues = useMemo(() => {
    if (date === "All") {
      return { startDate: "", endDate: "" }
    }
    const interval = getDateRange(date)
    return {
      startDate: interval.start.toISO(),
      endDate: interval.end.toISO(),
    }
  }, [date])

  const handleSetFilters = (newFilters) => {
    setFilters(newFilters)
  }

  const queryObject = useMemo(
    () => ({
      source,
      bodyPart,
      type,
      view,
      region,
      date,
    }),
    [source, bodyPart, type, view, region, date]
  )

  useEffect(() => {
    const data = { source, bodyPart, type, view, region, date }
    sessionStorage.setItem("queryObject", JSON.stringify(data))
  }, [source, bodyPart, type, view, region, date])

  const { data, isLoading, error, isError } = useErrorLogsData({
    page,
    limit,
    filters: {
      source,
      bodyPart,
      view,
      region,
      type,
      ...dateValues,
      ...generateFilter(filters),
    },
  })

  if (isError) {
    return (
      <div className="mt-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <div className="flex gap-2 justify-between items-center my-4">
        <h1 className="font-medium text-xl text-primary">Illustration Logs</h1>
        <div className="flex gap-3">
          <Dropdown
            label={"Source"}
            options={["Ramsoft", "Outside V3"]}
            setValue={setSource}
            value={source}
          />
          <Dropdown
            label={"BodyPart"}
            options={Object.values(bodyParts)}
            setValue={handleUpdateBodyPart}
            value={bodyPart}
          />
          {bodyPart === bodyParts.BRAIN && (
            <Dropdown
              label={"Type"}
              options={["All", "Lateral"]}
              setValue={setType}
              value={type}
            />
          )}
          {bodyPart === bodyParts.SPINE && (
            <>
              <Dropdown
                label={"View"}
                options={["All", "Sagittal", "Axial"]}
                setValue={setView}
                value={view}
              />
              <Dropdown
                label={"Region"}
                options={["All", "C-Spine", "L-Spine", "T-Spine"]}
                setValue={setRegion}
                value={region}
              />
            </>
          )}

          {bodyPart === bodyParts.KNEE && (
            <>
              <Dropdown
                label={"View"}
                options={[
                  "All",
                  "Deep",
                  "Superficial",
                  "Lateral",
                  "Frontal",
                  "Sagittal",
                ]}
                setValue={setView}
                value={view}
              />
            </>
          )}

          {bodyPart === bodyParts.SHOULDER && (
            <>
              <Dropdown
                label={"View"}
                options={["All", "Lateral", "Frontal"]}
                setValue={setView}
                value={view}
              />
            </>
          )}
          <Dropdown
            label={"Date"}
            options={[
              "Today",
              "Yesterday",
              "This Week",
              "Last Week",
              "This Month",
              "Last Month",
            ]}
            setValue={setDate}
            value={date}
          />
        </div>
      </div>
      <ErrorLogsTable
        data={isLoading ? [] : data}
        filters={filters}
        loading={isLoading}
        pageCount={isLoading ? 0 : 1}
        pagination={pagination}
        query={queryObject}
        setFilters={handleSetFilters}
        onPaginationChange={onPaginationChange}
      />
    </div>
  )
}

export default memo(ErrorLogs)
